import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertCircle, CheckCircle, Mail, Settings, Shield, Zap, RefreshCw, Radio, Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EmailService {
  id: string;
  name: string;
  type: 'notification_api' | 'resend' | 'sendgrid' | 'mailgun' | 'supabase';
  enabled: boolean;
  priority: number;
  config: {
    apiKey?: string;
    clientId?: string;
    secret?: string;
    fromEmail?: string;
    fromName?: string;
  };
  status: 'active' | 'inactive' | 'error';
  lastTested?: string;
  created_at: string;
  updated_at: string;
}

const AdminEmailServiceManagement = () => {
  const [emailServices, setEmailServices] = useState<EmailService[]>([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState<string | null>(null);
  const [editingService, setEditingService] = useState<EmailService | null>(null);
  const [configForm, setConfigForm] = useState<Record<string, string>>({});

  useEffect(() => {
    loadEmailServices();
  }, []);

  const loadEmailServices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('email_services')
        .select('*')
        .order('priority', { ascending: true });

      if (error) throw error;

      // If no services exist, create default ones
      if (!data || data.length === 0) {
        await createDefaultServices();
        return;
      }

      setEmailServices(data as EmailService[]);
    } catch (error) {
      console.error('Error loading email services:', error);
      toast.error('Failed to load email services');
    } finally {
      setLoading(false);
    }
  };

  const createDefaultServices = async () => {
    const defaultServices = [
      {
        name: 'Supabase (Default)',
        type: 'supabase',
        enabled: true, // Set Supabase as default enabled
        priority: 1,
        config: {
          fromName: 'Your App'
        },
        status: 'active'
      },
      {
        name: 'NotificationAPI',
        type: 'notification_api',
        enabled: false,
        priority: 2,
        config: {
          clientId: '',
          secret: '',
          fromName: 'Your App'
        },
        status: 'inactive'
      },
      {
        name: 'Resend',
        type: 'resend',
        enabled: false,
        priority: 3,
        config: {
          apiKey: '',
          fromEmail: 'noreply@yourdomain.com',
          fromName: 'Your App'
        },
        status: 'inactive'
      },
      {
        name: 'SendGrid',
        type: 'sendgrid',
        enabled: false,
        priority: 4,
        config: {
          apiKey: '',
          fromEmail: 'noreply@yourdomain.com',
          fromName: 'Your App'
        },
        status: 'inactive'
      }
    ];

    try {
      const { data, error } = await supabase
        .from('email_services')
        .insert(defaultServices)
        .select();

      if (error) throw error;
      setEmailServices(data as EmailService[]);
    } catch (error) {
      console.error('Error creating default services:', error);
      toast.error('Failed to create default email services');
    }
  };

  const updateEmailService = async (serviceId: string, updates: Partial<EmailService>) => {
    try {
      const { error } = await supabase
        .from('email_services')
        .update(updates)
        .eq('id', serviceId);

      if (error) throw error;

      setEmailServices(prev => prev.map(service => 
        service.id === serviceId ? { ...service, ...updates } : service
      ));

      toast.success('Email service updated successfully');
    } catch (error) {
      console.error('Error updating email service:', error);
      toast.error('Failed to update email service');
    }
  };

  const testEmailService = async (service: EmailService) => {
    try {
      setTesting(service.id);
      
      const { data, error } = await supabase.functions.invoke('test-email-service', {
        body: {
          serviceId: service.id,
          testEmail: 'admin@test.com'
        }
      });

      if (error) throw error;

      if (data.success) {
        await updateEmailService(service.id, { 
          status: 'active',
          lastTested: new Date().toISOString()
        });
        toast.success(`${service.name} test successful`);
      } else {
        await updateEmailService(service.id, { status: 'error' });
        toast.error(`${service.name} test failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Error testing email service:', error);
      await updateEmailService(service.id, { status: 'error' });
      toast.error(`Failed to test ${service.name}`);
    } finally {
      setTesting(null);
    }
  };

  const handlePriorityChange = async (serviceId: string, newPriority: number) => {
    await updateEmailService(serviceId, { priority: newPriority });
  };

  const handleEditService = (service: EmailService) => {
    setEditingService(service);
    setConfigForm(service.config || {});
  };

  const saveServiceConfig = async () => {
    if (!editingService) return;
    
    await updateEmailService(editingService.id, { config: configForm });
    setEditingService(null);
    setConfigForm({});
  };

  const handleToggleService = async (serviceId: string) => {
    // Since only one service can be enabled, we enable this one (trigger will disable others)
    await updateEmailService(serviceId, { enabled: true });
    // Refresh the list to show updated states
    setTimeout(loadEmailServices, 500);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      error: 'bg-red-500/20 text-red-400 border-red-500/30',
      inactive: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };
    
    return variants[status as keyof typeof variants] || variants.inactive;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-purple-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Email Service Management</h2>
          <p className="text-gray-400">Configure multiple email providers with automatic failover</p>
        </div>
        <Button 
          onClick={loadEmailServices}
          variant="outline"
          className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="services" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-slate-800/50 border border-purple-500/20">
          <TabsTrigger value="services" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
            <Mail className="h-4 w-4 mr-2" />
            Email Services
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
            <Settings className="h-4 w-4 mr-2" />
            Failover Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          <div className="grid gap-4">
            {emailServices.map((service) => (
              <Card key={service.id} className="bg-slate-800/50 border-purple-500/20">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(service.status)}
                        <CardTitle className="text-lg text-white">{service.name}</CardTitle>
                      </div>
                      <Badge className={`${getStatusBadge(service.status)} border`}>
                        {service.status}
                      </Badge>
                      <Badge variant="outline" className="border-blue-500/30 text-blue-400">
                        Priority {service.priority}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="email-service"
                        checked={service.enabled}
                        onChange={() => handleToggleService(service.id)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-300">
                        {service.enabled ? 'Active' : 'Select'}
                      </span>
                      <Button
                        onClick={() => handleEditService(service)}
                        size="sm"
                        variant="ghost"
                        className="text-gray-400 hover:text-white"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => testEmailService(service)}
                        disabled={testing === service.id || service.type === 'supabase'}
                        size="sm"
                        variant="outline"
                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      >
                        {testing === service.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4" />
                        )}
                        {service.type === 'supabase' ? 'Built-in' : 'Test'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-400">Type</Label>
                      <p className="text-white capitalize">{service.type.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-400">Last Tested</Label>
                      <p className="text-white">
                        {service.lastTested 
                          ? new Date(service.lastTested).toLocaleDateString()
                          : 'Never'
                        }
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-400">From Email</Label>
                      <p className="text-white">{service.config.fromEmail || 'Not configured'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-400">Priority Order</Label>
                      <Select
                        value={service.priority.toString()}
                        onValueChange={(value) => handlePriorityChange(service.id, parseInt(value))}
                      >
                        <SelectTrigger className="w-20 bg-slate-700/50 border-purple-500/30">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map(priority => (
                            <SelectItem key={priority} value={priority.toString()}>
                              {priority}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-slate-700/30 rounded border border-purple-500/20">
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="h-4 w-4 text-purple-400" />
                      <Label className="text-sm text-gray-400">Configuration Status</Label>
                    </div>
                    <div className="text-xs text-gray-300">
                      {service.type === 'supabase' && (
                        <>Built-in Supabase Email ✓</>
                      )}
                      {service.type === 'notification_api' && (
                        <>Client ID and Secret {service.config.clientId && service.config.secret ? '✓' : '✗'}</>
                      )}
                      {service.type !== 'notification_api' && service.type !== 'supabase' && (
                        <>API Key {service.config.apiKey ? '✓' : '✗'}</>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card className="bg-slate-800/50 border-purple-500/20">
            <CardHeader>
              <CardTitle className="text-white">Failover Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Retry Attempts</Label>
                  <Input
                    type="number"
                    defaultValue="3"
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Timeout (seconds)</Label>
                  <Input
                    type="number"
                    defaultValue="30"
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-white mb-3">Email Service Selection</h3>
                <div className="space-y-2 text-sm text-gray-300">
                  <p>• Only one email service can be active at a time</p>
                  <p>• Supabase is the default built-in email service (no configuration required)</p>
                  <p>• External services require proper API key configuration</p>
                  <p>• Test services before enabling them for production use</p>
                  <p>• If external service fails, emails will fall back to Supabase</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Configuration Dialog */}
      <Dialog open={!!editingService} onOpenChange={() => setEditingService(null)}>
        <DialogContent className="bg-slate-800 border-purple-500/20 text-white">
          <DialogHeader>
            <DialogTitle>Configure {editingService?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {editingService?.type === 'notification_api' && (
              <>
                <div>
                  <Label className="text-gray-400">Client ID</Label>
                  <Input
                    value={configForm.clientId || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, clientId: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="Enter NotificationAPI Client ID"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Secret</Label>
                  <Input
                    type="password"
                    value={configForm.secret || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, secret: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="Enter NotificationAPI Secret"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">From Name</Label>
                  <Input
                    value={configForm.fromName || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, fromName: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="Your App Name"
                  />
                </div>
              </>
            )}
            
            {(editingService?.type === 'resend' || editingService?.type === 'sendgrid') && (
              <>
                <div>
                  <Label className="text-gray-400">API Key</Label>
                  <Input
                    type="password"
                    value={configForm.apiKey || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, apiKey: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="Enter API Key"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">From Email</Label>
                  <Input
                    type="email"
                    value={configForm.fromEmail || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, fromEmail: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="noreply@yourdomain.com"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">From Name</Label>
                  <Input
                    value={configForm.fromName || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, fromName: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="Your App Name"
                  />
                </div>
              </>
            )}
            
            {editingService?.type === 'mailgun' && (
              <>
                <div>
                  <Label className="text-gray-400">API Key</Label>
                  <Input
                    type="password"
                    value={configForm.apiKey || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, apiKey: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="Enter Mailgun API Key"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Domain</Label>
                  <Input
                    value={configForm.domain || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, domain: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="yourdomain.com"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">From Email</Label>
                  <Input
                    type="email"
                    value={configForm.fromEmail || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, fromEmail: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="noreply@yourdomain.com"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">From Name</Label>
                  <Input
                    value={configForm.fromName || ''}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, fromName: e.target.value }))}
                    className="bg-slate-700/50 border-purple-500/30 text-white"
                    placeholder="Your App Name"
                  />
                </div>
              </>
            )}
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                onClick={() => setEditingService(null)}
                variant="outline"
                className="border-gray-500/30 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={saveServiceConfig}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Save Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailServiceManagement;